/**
 * Hook to fetch and manage the worker's task list.
 * Supports offline mode by caching tasks in IndexedDB.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchMyTasks, type MyTasksResponse, type WorkerTask } from '../lib/api';
import { cacheTaskList, getCachedTaskList, type CachedTask } from '../lib/db';
import { useOnlineStatus } from './useOnlineStatus';

/**
 * Convert WorkerTask to CachedTask for storage.
 */
function toCachedTask(task: WorkerTask): Omit<CachedTask, 'cachedAt'> {
  return {
    id: task.id,
    partNumber: task.partNumber,
    description: task.description,
    laborBucket: task.laborBucket,
    projectName: task.projectName,
    gpSalesOrderNumber: task.gpSalesOrderNumber,
    priority: task.priority,
    estimatedMinutes: task.estimatedMinutes,
    actualMinutes: task.actualMinutes,
    estimatedMinutesMin: task.estimatedMinutesMin,
    estimatedMinutesMax: task.estimatedMinutesMax,
    status: task.status,
    scheduledStart: task.scheduledStart,
  };
}

/**
 * Convert CachedTask back to WorkerTask for UI.
 * Stale IndexedDB entries (cached before this feature) may lack the new fields.
 * Fall back to the pre-existing midpoint estimate rather than 0 to avoid "Est: 0m".
 */
function toWorkerTask(cached: CachedTask): WorkerTask {
  const fallbackEstimate = cached.estimatedMinutes;
  return {
    id: cached.id,
    partNumber: cached.partNumber,
    description: cached.description,
    laborBucket: cached.laborBucket as WorkerTask['laborBucket'],
    projectName: cached.projectName,
    gpSalesOrderNumber: cached.gpSalesOrderNumber,
    priority: cached.priority,
    estimatedMinutes: cached.estimatedMinutes,
    actualMinutes: cached.actualMinutes ?? 0,
    estimatedMinutesMin: cached.estimatedMinutesMin ?? fallbackEstimate,
    estimatedMinutesMax: cached.estimatedMinutesMax ?? fallbackEstimate,
    status: cached.status as WorkerTask['status'],
    scheduledStart: cached.scheduledStart,
  };
}

export function useTasks() {
  const isOnline = useOnlineStatus();

  return useQuery<MyTasksResponse>({
    queryKey: ['my-tasks'],
    queryFn: async (): Promise<MyTasksResponse> => {
      // When offline, return cached data
      if (!isOnline) {
        const cached = await getCachedTaskList();
        return {
          data: cached.map(toWorkerTask),
          activeTask: null, // Active task info not available offline
        };
      }

      // Fetch from API when online
      const response = await fetchMyTasks();

      // Cache tasks for offline use (fire and forget)
      cacheTaskList(response.data.map(toCachedTask)).catch((err) => {
        console.warn('Failed to cache tasks:', err);
      });

      return response;
    },
    staleTime: 30_000, // 30 seconds
    refetchInterval: isOnline ? 60_000 : false, // Only refetch when online
    refetchOnWindowFocus: isOnline,
    retry: isOnline ? 3 : 0, // Don't retry when offline
  });
}
